import React, { useRef, useCallback, useEffect, useState } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNotebook } from '@/contexts/NotebookContext';
import { NotebookCell as NotebookCellType, CellOutput } from '@/services/notebooksApi';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Play,
  Trash2,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Loader2,
  Code,
  FileText,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

interface NotebookCellProps {
  cell: NotebookCellType;
  isSelected: boolean;
  isExecuting: boolean;
}

// =============================================================================
// Cell Output Renderer
// =============================================================================

function CellOutputRenderer({ output }: { output: CellOutput }) {
  // Handle raw pyodide results (not in Jupyter format)
  // These have stdout/value/stderr but NO output_type
  const rawOutput = output as unknown as { stdout?: string; value?: unknown; stderr?: string; output_type?: string };
  const isRawPyodideOutput = !rawOutput.output_type && (rawOutput.value !== undefined || rawOutput.stdout !== undefined);

  if (isRawPyodideOutput) {
    // Don't show anything if stdout is empty string and value is undefined/null
    const hasContent = (rawOutput.stdout && rawOutput.stdout.length > 0) ||
                       (rawOutput.stderr && rawOutput.stderr.length > 0) ||
                       (rawOutput.value !== undefined && rawOutput.value !== null);

    if (!hasContent) {
      return null;
    }

    return (
      <div className="text-sm font-mono space-y-1">
        {rawOutput.stdout && rawOutput.stdout.length > 0 && (
          <pre className="whitespace-pre-wrap text-muted-foreground">{rawOutput.stdout}</pre>
        )}
        {rawOutput.stderr && rawOutput.stderr.length > 0 && (
          <pre className="whitespace-pre-wrap text-red-500">{rawOutput.stderr}</pre>
        )}
        {rawOutput.value !== undefined && rawOutput.value !== null && (
          <pre className="whitespace-pre-wrap text-foreground">
            {typeof rawOutput.value === 'string'
              ? rawOutput.value
              : JSON.stringify(rawOutput.value, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  switch (output.output_type) {
    case 'stream':
      return (
        <pre className="whitespace-pre-wrap text-sm font-mono text-muted-foreground">
          {output.text}
        </pre>
      );

    case 'execute_result':
      return (
        <pre className="whitespace-pre-wrap text-sm font-mono">
          {output.data?.['text/plain'] || JSON.stringify(output.data, null, 2)}
        </pre>
      );

    case 'error':
      return (
        <div className="text-sm font-mono">
          <span className="text-red-600 font-semibold">
            {output.ename}: {output.evalue}
          </span>
          {output.traceback && output.traceback.length > 0 && (
            <pre className="mt-1 text-red-500 text-xs whitespace-pre-wrap">
              {output.traceback.join('\n')}
            </pre>
          )}
        </div>
      );

    case 'display_data':
      // Handle image/png (matplotlib figures)
      if (output.data?.['image/png']) {
        const imgSrc = output.data['image/png'].startsWith('data:')
          ? output.data['image/png']
          : `data:image/png;base64,${output.data['image/png']}`;
        return (
          <div className="flex justify-center my-2">
            <img
              src={imgSrc}
              alt="matplotlib figure"
              className="max-w-full border rounded bg-white"
            />
          </div>
        );
      }
      // Handle text/html
      if (output.data?.['text/html']) {
        return (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: output.data['text/html'] }}
          />
        );
      }
      // Fallback to text/plain
      return (
        <pre className="whitespace-pre-wrap text-sm font-mono">
          {output.data?.['text/plain'] || JSON.stringify(output.data, null, 2)}
        </pre>
      );

    default:
      return null;
  }
}

// =============================================================================
// Markdown Cell Renderer
// =============================================================================

function MarkdownRenderer({ source, isEditing }: { source: string; isEditing: boolean }) {
  if (isEditing) {
    return null; // Editor handles display
  }

  const content = source || '*Empty markdown cell*';

  return (
    <div className="prose prose-sm max-w-none p-4 dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom code block styling
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match && !className;
            return isInline ? (
              <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                {children}
              </code>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Better pre styling for code blocks
          pre({ children, ...props }) {
            return (
              <pre className="bg-muted p-3 rounded-md overflow-x-auto text-sm" {...props}>
                {children}
              </pre>
            );
          },
          // Make links open in new tab
          a({ children, href, ...props }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" {...props}>
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function NotebookCell({
  cell,
  isSelected,
  isExecuting,
}: NotebookCellProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [localSource, setLocalSource] = useState(cell.source);

  const {
    state: { dirtySourceMap, editingCellId, notebook },
    selectCell,
    editCell,
    updateCellSource,
    saveCellSource,
    removeCell,
    moveCell,
    runCell,
  } = useNotebook();

  // Determine if cell is first or last for move button state
  const cellIndex = notebook?.cells.findIndex((c) => c.id === cell.id) ?? -1;
  const isFirstCell = cellIndex === 0;
  const isLastCell = notebook ? cellIndex === notebook.cells.length - 1 : true;

  const isEditing = editingCellId === cell.id;
  const isDirty = dirtySourceMap[cell.id] !== undefined;

  // Sync local source with cell source
  useEffect(() => {
    if (!isDirty) {
      setLocalSource(cell.source);
    }
  }, [cell.source, isDirty]);

  // Use ref to always have latest source for blur handler
  const localSourceRef = useRef(localSource);
  useEffect(() => {
    localSourceRef.current = localSource;
  }, [localSource]);

  // Handle editor blur - auto-save and exit edit mode for markdown
  const handleEditorBlur = useCallback(() => {
    // Always save current source on blur (pass directly to avoid stale state)
    saveCellSource(cell.id, localSourceRef.current);
    // For markdown cells, exit edit mode to show rendered view
    if (cell.cell_type === 'markdown') {
      editCell(null);
    }
  }, [cell.id, cell.cell_type, saveCellSource, editCell]);

  // Handle editor mount
  const handleEditorMount = useCallback(
    (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
      editorRef.current = editor;

      // Focus editor when entering edit mode
      if (isEditing) {
        editor.focus();
      }

      // Handle blur for auto-save
      editor.onDidBlurEditorWidget(() => {
        handleEditorBlur();
      });

      // Handle Shift+Enter to run
      editor.addCommand(
        // Shift+Enter
        monaco.KeyMod.Shift | monaco.KeyCode.Enter,
        () => {
          runCell(cell.id);
        }
      );

      // Handle Escape to exit editing (and save)
      editor.addCommand(monaco.KeyCode.Escape, () => {
        saveCellSource(cell.id, localSourceRef.current);
        editCell(null);
      });
    },
    [isEditing, cell.id, runCell, editCell, saveCellSource, handleEditorBlur]
  );

  // Handle source change
  const handleSourceChange = useCallback(
    (value: string | undefined) => {
      const newSource = value || '';
      setLocalSource(newSource);
      updateCellSource(cell.id, newSource);
    },
    [cell.id, updateCellSource]
  );

  // Handle cell click
  const handleCellClick = useCallback(() => {
    selectCell(cell.id);
  }, [cell.id, selectCell]);

  // Handle double-click to edit
  const handleDoubleClick = useCallback(() => {
    selectCell(cell.id);
    editCell(cell.id);
  }, [cell.id, selectCell, editCell]);

  // Calculate editor height based on content
  const lineCount = (localSource || '').split('\n').length;
  const editorHeight = Math.max(80, Math.min(500, lineCount * 20 + 24));

  return (
    <div
      className={cn(
        'group relative border rounded-lg transition-colors',
        isSelected ? 'border-primary ring-1 ring-primary/20' : 'border-border',
        isExecuting && 'border-yellow-500 ring-1 ring-yellow-500/20'
      )}
      onClick={handleCellClick}
    >
      {/* Cell toolbar */}
      <div
        className={cn(
          'absolute -left-8 top-0 flex flex-col items-center gap-0.5',
          'opacity-0 group-hover:opacity-100 transition-opacity'
        )}
      >
        {/* Execution count */}
        {cell.cell_type === 'code' && (
          <div className="text-xs text-muted-foreground font-mono w-6 text-right">
            {isExecuting ? (
              <Loader2 className="w-3 h-3 animate-spin inline" />
            ) : cell.execution_count ? (
              `[${cell.execution_count}]`
            ) : (
              '[ ]'
            )}
          </div>
        )}
      </div>

      {/* Cell header */}
      <div className="flex items-center gap-1 px-2 py-1 border-b bg-muted/30">
        {/* Cell type icon */}
        {cell.cell_type === 'code' ? (
          <Code className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <FileText className="w-3.5 h-3.5 text-muted-foreground" />
        )}

        <span className="text-xs text-muted-foreground capitalize flex-1">
          {cell.cell_type}
          {isDirty && <span className="ml-1">•</span>}
        </span>

        {/* Run button */}
        {cell.cell_type === 'code' && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              runCell(cell.id);
            }}
            disabled={isExecuting}
          >
            {isExecuting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
          </Button>
        )}

        {/* More options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => runCell(cell.id)} disabled={cell.cell_type !== 'code'}>
              <Play className="w-4 h-4 mr-2" />
              Run Cell
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => editCell(cell.id)}>
              <Code className="w-4 h-4 mr-2" />
              Edit Cell
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => moveCell(cell.id, 'up')}
              disabled={isFirstCell}
            >
              <ArrowUp className="w-4 h-4 mr-2" />
              Move Up
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => moveCell(cell.id, 'down')}
              disabled={isLastCell}
            >
              <ArrowDown className="w-4 h-4 mr-2" />
              Move Down
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => removeCell(cell.id)} className="text-destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Cell
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Cell content */}
      <div onDoubleClick={handleDoubleClick}>
        {cell.cell_type === 'code' ? (
          // Code cell - always show editor
          <div className="relative" style={{ height: editorHeight }}>
            {/* Loading overlay */}
            {isExecuting && (
              <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Running...</span>
                </div>
              </div>
            )}
            <Editor
              value={localSource}
              onChange={handleSourceChange}
              language="python"
              theme="vs"
              options={{
                minimap: { enabled: false },
                lineNumbers: 'on',
                wordWrap: 'on',
                fontSize: 13,
                fontFamily: '"Fira Code", "Monaco", "Menlo", monospace',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 4,
                insertSpaces: true,
                folding: false,
                glyphMargin: false,
                lineDecorationsWidth: 8,
                lineNumbersMinChars: 3,
                renderLineHighlight: 'line',
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'hidden',
                  verticalScrollbarSize: 8,
                },
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                padding: { top: 8, bottom: 8 },
              }}
              onMount={handleEditorMount}
            />
          </div>
        ) : isEditing ? (
          // Markdown cell - editing mode
          <div style={{ height: editorHeight }}>
            <Editor
              value={localSource}
              onChange={handleSourceChange}
              language="markdown"
              theme="vs"
              options={{
                minimap: { enabled: false },
                lineNumbers: 'off',
                wordWrap: 'on',
                fontSize: 13,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                padding: { top: 8, bottom: 8 },
              }}
              onMount={handleEditorMount}
            />
          </div>
        ) : (
          // Markdown cell - rendered mode
          <MarkdownRenderer source={localSource} isEditing={false} />
        )}
      </div>

      {/* Cell outputs */}
      {cell.cell_type === 'code' && cell.outputs && cell.outputs.length > 0 && (
        <div className="border-t bg-muted/20 p-3 space-y-2">
          {cell.outputs.map((output, idx) => (
            <CellOutputRenderer key={idx} output={output} />
          ))}
        </div>
      )}
    </div>
  );
}
